import { startCase } from 'lodash';
import { useMemo } from 'react';

import type {
  Item,
  SelectionList,
  SelectionListIdNames,
} from '@revelio/iso-utility';

import { useSelectionLists } from '../../../../shared/selection-lists/hooks/useSelectionLists';
import { SelectionCategoryLabelOverrides } from '../../../../shared/selection-lists/selection-category-label-overrides';
import type { GqlSelectionListKey } from '../../../../shared/selection-lists/types';
import { useRoleSelectionLists } from '../../api/useRoleSelectionLists';
import { useSkillSelectionLists } from '../../api/useSkillSelectionLists';
import {
  selectModelVersions,
  useDataBuilderSelector,
} from '../../data-builder.store';
import { isV3Taxonomy } from '../../utils/job-taxonomy-version-migration';
import {
  addParentFieldAliases,
  getRoleHierarchy,
  getSkillHierarchy,
  resolveHierarchyKeys,
  toApiKey,
  validateHierarchy,
} from './data-builder-selection-list-mapping';

export type UseDataBuilderSelectionListsOptions = {
  apiKeys: string[];
};

export function useDataBuilderSelectionLists({
  apiKeys,
}: UseDataBuilderSelectionListsOptions) {
  const modelVersions = useDataBuilderSelector(selectModelVersions);
  const jobTaxonomyVersion = modelVersions.job_taxonomy_version?.toString();
  const skillTaxonomyVersion = modelVersions.skill_taxonomy_version?.toString();
  const jobTaxonomyId = modelVersions.job_taxonomy_id;
  const skillTaxonomyId = modelVersions.skill_taxonomy_id;
  const isV3Job = isV3Taxonomy(jobTaxonomyVersion);
  const isV3Skill = isV3Taxonomy(skillTaxonomyVersion);
  const resolved = useMemo(
    () => resolveHierarchyKeys(apiKeys, isV3Job, isV3Skill),
    [apiKeys, isV3Job, isV3Skill]
  );

  const roleFetchKeys = useMemo(
    () => resolved.role.map(String),
    [resolved.role]
  );

  const { roleSelectionLists } = useRoleSelectionLists(roleFetchKeys, {
    jobTaxonomyVersion,
    taxonomyId: jobTaxonomyId as number | undefined,
  });

  const v3SkillFetchKeys = useMemo(
    () => (isV3Skill ? resolved.skill.map(String) : []),
    [isV3Skill, resolved.skill]
  );

  const { skillSelectionLists } = useSkillSelectionLists(v3SkillFetchKeys, {
    skillTaxonomyVersion,
    taxonomyId: skillTaxonomyId as number | undefined,
  });

  // V2 skill keys are standard GQL keys; merge them with other GQL keys
  const gqlFetchKeys = useMemo(() => {
    const keys = [...resolved.gql];
    if (!isV3Skill) {
      keys.push(...resolved.skill);
    }
    return keys as GqlSelectionListKey[];
  }, [resolved.gql, resolved.skill, isV3Skill]);

  const nonRoleSelectionListState = useSelectionLists(gqlFetchKeys);

  const selectionLists = useMemo(() => {
    const byId = new Map<string, SelectionList>();

    if (nonRoleSelectionListState) {
      for (const key of gqlFetchKeys) {
        const list =
          nonRoleSelectionListState[
            key as keyof typeof nonRoleSelectionListState
          ];
        if (list) {
          const normalizedValue = (list[0] as SelectionList)?.label
            ? list
            : list.map((item) => ({
                ...item,
                label: item.longName,
              }));

          byId.set(String(key), {
            id: key,
            label: key,
            value: addParentFieldAliases(normalizedValue as Item[]),
          } as SelectionList);
        }
      }
    }

    if (roleSelectionLists) {
      Object.values(roleSelectionLists).forEach((list) => {
        if (list) byId.set(String(list.id), list as SelectionList);
      });
    }

    if (skillSelectionLists) {
      Object.values(skillSelectionLists).forEach((list) => {
        if (list) byId.set(String(list.id), list);
      });
    }

    return resolved.all
      .map((key) => byId.get(String(key)))
      .filter((l): l is SelectionList => !!l);
  }, [
    resolved.all,
    gqlFetchKeys,
    nonRoleSelectionListState,
    roleSelectionLists,
    skillSelectionLists,
  ]);

  const branchLabels = useMemo(
    () =>
      selectionLists.map(
        (l) =>
          SelectionCategoryLabelOverrides[String(l.id)] ??
          startCase(toApiKey(l.id as SelectionListIdNames))
      ),
    [selectionLists]
  );

  if (process.env.NODE_ENV === 'development') {
    if (resolved.role.length > 0) {
      const roleWarning = validateHierarchy(
        resolved.role,
        getRoleHierarchy(isV3Job)
      );
      if (roleWarning) console.warn(roleWarning);
    }
    if (resolved.skill.length > 0) {
      const skillWarning = validateHierarchy(
        resolved.skill,
        getSkillHierarchy(isV3Skill)
      );
      if (skillWarning) console.warn(skillWarning);
    }
  }

  return { selectionLists, branchLabels, resolvedKeys: resolved.all };
}
